import { Stack, Typography } from '@mui/material'
import { FC, MouseEventHandler, useMemo } from 'react'
import { useTheme, styled } from '@mui/material/styles'
import { useEditOrderItem, useOrdering, useCurrentStore } from 'hooks'
import { ItemImage } from 'components/ordering/ItemImage'
import { OrderItem, OrderModifier } from 'models'
import { isEmpty } from 'lodash'
import {
  checkCanShowEditItemDialog,
  getOItemDisplayName,
  loadMenuItemByOrderItem,
} from 'services'
import { OrderItemQuantity } from 'components/ordering/OrderItemQuantity'

const ItemLine = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(1),
}))

interface OptionLineProps {
  name: string
  amount: number
}

const OptionLine = ({ name, amount }: OptionLineProps) => {
  const { formatCurrency } = useCurrentStore()

  return (
    <ItemLine sx={{ maxWidth: '100%' }}>
      <Typography
        variant="body2"
        sx={{
          maxWidth: '100%',
          flexGrow: 1,
          flexBasis: 0,
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        }}
        color="secondary"
      >
        {name}
      </Typography>
      <Typography variant="body2" textAlign="right" color="secondary">
        {formatCurrency(amount)}
      </Typography>
    </ItemLine>
  )
}

interface OrderItemCardProps {
  orderItem: OrderItem
  readonly?: boolean
}

interface OptionLineProps {
  key: string
  name: string
  amount: number
}

export const OrderItemCard: FC<OrderItemCardProps> = ({
  orderItem: oItem,
  readonly,
}) => {
  const theme = useTheme()
  const { formatCurrency, store } = useCurrentStore()
  const { order, saveOrder } = useOrdering()
  const { editOrderItem } = useEditOrderItem()

  const optionLines = useMemo(() => {
    const lines: OptionLineProps[] = []
    if (!isEmpty(oItem.note) || (oItem.price_note ?? 0) > 0) {
      lines.push({
        key: 'note',
        name: oItem.note ?? '',
        amount: (oItem.price_note ?? 0) * oItem.count,
      })
    }

    const addModifierLines = (
      modifierLines: OptionLineProps[],
      oModifier: OrderModifier,
    ) => {
      oModifier.options?.forEach((oOption) =>
        modifierLines.push({
          key: oOption.id,
          name: getOItemDisplayName(oModifier, oOption),
          amount: oOption.subtotal ?? 0,
        }),
      )
    }

    const addOptionLines = (
      optionLines: OptionLineProps[],
      modifierLines: OptionLineProps[],
      key: string,
    ) => {
      optionLines.push(
        modifierLines.reduce(
          (prev, curr) => ({
            key: key,
            name:
              prev.name.length === 0 ? curr.name : `${prev.name}, ${curr.name}`,
            amount: prev.amount + curr.amount,
          }),
          {
            key: key,
            name: '',
            amount: 0,
          },
        ),
      )
    }

    const modifierLines: OptionLineProps[] = []
    const globalModifierLines: OptionLineProps[] = []
    oItem.modifiers?.forEach((oModifier) => {
      if (oModifier.global) {
        addModifierLines(globalModifierLines, oModifier)
      } else {
        addModifierLines(modifierLines, oModifier)
      }
    })
    if (modifierLines.length > 0) {
      addOptionLines(lines, modifierLines, 'modifiers')
    }
    if (globalModifierLines.length > 0) {
      addOptionLines(lines, globalModifierLines, 'globalModifiers')
    }
    return lines
  }, [oItem])

  const decreaseQuantity: MouseEventHandler = (event) => {
    event.stopPropagation()
    if (oItem.count > 1) {
      saveOrderItem({ ...oItem, count: oItem.count - 1 })
    } else if (order) {
      saveOrder({
        ...order,
        items: order.items.filter((oi) => oi.id !== oItem.id),
      })
    }
  }

  const increaseQuantity: MouseEventHandler = (event) => {
    event.stopPropagation()
    if (oItem.count < 99) {
      saveOrderItem({ ...oItem, count: oItem.count + 1 })
    }
  }

  const saveOrderItem = (updatedOItem: OrderItem) => {
    if (!order) return
    const oiIndex = order.items.findIndex((oi) => oi.id === oItem.id)
    const oItems = [...order.items]
    oItems[oiIndex] = updatedOItem
    saveOrder({ ...order, items: oItems })
  }

  const canShowEditItemDialog = useMemo(() => {
    const menuItem = loadMenuItemByOrderItem(oItem, store.categories ?? [])
    if (!menuItem) return false

    return !readonly && checkCanShowEditItemDialog(menuItem, store)
  }, [oItem, store, readonly])

  return (
    <Stack
      sx={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'stretch',
        borderRadius: 4,
        gap: 2,
        padding: 1.5,
        minWidth: 280,
        backgroundColor: theme.palette.primary.light,
        overflow: 'hidden',
        height: 140,
        minHeight: 140,
        [theme.breakpoints.down('sm')]: {
          height: 128,
          minHeight: 128,
          borderRadius: 3,
          gap: 1.5,
        },
        ...(canShowEditItemDialog && {
          '&:hover': {
            cursor: 'pointer',
            backgroundColor: theme.palette.primary['200'],
          },
          '&:active': {
            cursor: 'pointer',
            backgroundColor: theme.palette.primary.light,
          },
        }),
      }}
      onClick={canShowEditItemDialog ? () => editOrderItem(oItem) : undefined}
    >
      <ItemImage src={oItem.image_url} />
      <Stack
        sx={{
          flexGrow: 1,
          flexDirection: 'column',
          justifyContent: 'start',
          alignItems: 'stretch',
          paddingY: 0.5,
          gap: 1,
          height: '100%',
          width: `calc(100% - 100px - ${theme.spacing(2)})`,
          [theme.breakpoints.down('sm')]: {
            width: `calc(100% - 80px - ${theme.spacing(1.5)})`,
          },
        }}
      >
        <ItemLine>
          <Typography variant="h4" sx={{ flexGrow: 1 }}>
            {oItem.name}
          </Typography>
          <Typography variant="h4" textAlign="right" color="primary">
            {formatCurrency(oItem.subtotal)}
          </Typography>
        </ItemLine>
        <Stack
          sx={{
            // flexGrow: 1,
            overflow: 'hidden',
            gap: 0.5,
            height: 48,
            [theme.breakpoints.down('sm')]: {
              height: 44,
            },
          }}
        >
          {optionLines?.map((line) => <OptionLine {...line} />)}
        </Stack>
        <OrderItemQuantity
          allowDelete
          quantity={oItem.count}
          increaseQuantity={increaseQuantity}
          decreaseQuantity={decreaseQuantity}
          readonly={readonly}
        />
      </Stack>
    </Stack>
  )
}
